import { useEffect, useCallback } from 'react';

/**
 * Hook for handling keyboard navigation
 * @param {Object} handlers - Object mapping key combinations to handler functions
 * @param {Array} dependencies - Dependencies array for the effect
 * 
 * Example usage:
 * useKeyboardNavigation({
 *   'Escape': handleClose,
 *   'Enter': handleSubmit,
 *   'ArrowUp': handleUp,
 *   'ArrowDown': handleDown
 * }, [handleClose, handleSubmit]);
 */
export function useKeyboardNavigation(handlers, dependencies = []) {
  const handleKeyDown = useCallback((event) => {
    const handler = handlers[event.key];
    if (handler) {
      handler(event);
    }
  }, [handlers]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Hook for trapping focus within a container (useful for modals)
 * @param {Object} ref - React ref to the container element
 * @param {Boolean} isActive - Whether focus trap is active
 */
export function useFocusTrap(ref, isActive) {
  useEffect(() => {
    if (!isActive || !ref.current) return;

    const container = ref.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element when trap activates
    firstElement.focus();

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, [ref, isActive]);
}

/**
 * Hook for managing focus return when closing a modal/dialog
 * Returns functions to save and restore focus
 */
export function useFocusReturn() {
  const previousFocus = useCallback(() => {
    return document.activeElement;
  }, []);

  const saveFocus = useCallback(() => {
    return document.activeElement;
  }, []);

  const restoreFocus = useCallback((element) => {
    if (element && element.focus) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        element.focus();
      }, 0);
    }
  }, []);

  return { saveFocus, restoreFocus, previousFocus };
}

/**
 * Hook for announcing dynamic content to screen readers
 * @param {String} message - Message to announce
 * @param {String} politeness - 'polite' or 'assertive'
 */
export function useAnnounce(message, politeness = 'polite') {
  useEffect(() => {
    if (!message) return;

    // Create or find the live region
    let liveRegion = document.getElementById('a11y-announcer');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'a11y-announcer';
      liveRegion.setAttribute('aria-live', politeness);
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('class', 'sr-only');
      document.body.appendChild(liveRegion);
    }

    // Update the message
    liveRegion.textContent = message;

    // Clear after announcement
    const timeout = setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);

    return () => clearTimeout(timeout);
  }, [message, politeness]);
}
